import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Edit,
  Clock,
  MapPin,
  Tag,
  Star,
  Calendar,
  DollarSign,
  Image,
  List,
  CheckCircle,
  XCircle,
  Globe,
  Archive,
  Loader2,
  Plus,
  MoreHorizontal,
  Trash2,
  Users,
  Settings2,
  FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PackageModal } from '@/components/travel/packages/package-modal';
import { PricingModal } from '@/components/travel/packages/pricing-modal';
import { ItineraryModal } from '@/components/travel/packages/itinerary-modal';
import { MediaModal } from '@/components/travel/packages/media-modal';
import { InclusionExclusionModal } from '@/components/travel/packages/inclusion-exclusion-modal';
import { DepartureModal } from '@/components/travel/packages/departure-modal';
import { AllocationConfigSection } from '@/components/travel/packages/allocation-config-section';
import { OptionGroupsSection } from '@/components/travel/packages/option-groups-section';
import { VariantSection } from '@/components/travel/packages/variant-section';
import { trpc } from '@/lib/trpc';
import { formatCurrency, cn } from '@/lib/utils';

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PUBLISHED: 'bg-green-100 text-green-700',
  ARCHIVED: 'bg-red-100 text-red-700',
};

const statusLabelKeys: Record<string, string> = {
  DRAFT: 'status.draft',
  PUBLISHED: 'status.published',
  ARCHIVED: 'status.archived',
};

const difficultyColors: Record<string, string> = {
  EASY: 'bg-green-100 text-green-700',
  MODERATE: 'bg-blue-100 text-blue-700',
  CHALLENGING: 'bg-orange-100 text-orange-700',
  DIFFICULT: 'bg-red-100 text-red-700',
};

type PackageDifficulty = 'EASY' | 'MODERATE' | 'CHALLENGING' | 'DIFFICULT';
type PackageStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

type ItineraryDay = {
  id: string;
  dayNumber: number;
  title: string;
  description: string | null;
  activities: string[];
  meals: string[];
  accommodation: string | null;
};

type MediaItem = {
  id: string;
  url: string;
  alt: string | null;
  caption: string | null;
  isPrimary: boolean;
};

type DepartureStatus = 'OPEN' | 'LIMITED' | 'SOLD_OUT' | 'CANCELLED';

type DepartureItem = {
  id: string;
  departureDate: Date;
  returnDate: Date;
  capacity: number;
  bookedCount: number;
  status: DepartureStatus;
  isGuaranteed: boolean;
  notes: string | null;
};

type TabId = 'overview' | 'options' | 'pricing' | 'media' | 'departures';

const tabDefs: { id: TabId; labelKey: string; icon: React.ElementType }[] = [
  { id: 'overview', labelKey: 'tabs.overview', icon: FileText },
  { id: 'options', labelKey: 'tabs.optionsVariants', icon: Settings2 },
  { id: 'pricing', labelKey: 'tabs.pricing', icon: DollarSign },
  { id: 'media', labelKey: 'tabs.media', icon: Image },
  { id: 'departures', labelKey: 'tabs.departures', icon: Calendar },
];

export function PackageDetailPage() {
  const { t } = useTranslation('packages');
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isItineraryModalOpen, setIsItineraryModalOpen] = useState(false);
  const [editingItinerary, setEditingItinerary] = useState<ItineraryDay | undefined>();
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [editingMedia, setEditingMedia] = useState<MediaItem | undefined>();
  const [isInclusionModalOpen, setIsInclusionModalOpen] = useState(false);
  const [isExclusionModalOpen, setIsExclusionModalOpen] = useState(false);
  const [isDepartureModalOpen, setIsDepartureModalOpen] = useState(false);
  const [editingDeparture, setEditingDeparture] = useState<DepartureItem | undefined>();

  const { data: pkg, isLoading, error } = trpc.package.byId.useQuery(id!, {
    enabled: !!id,
  });

  const utils = trpc.useUtils();

  const updateStatusMutation = trpc.package.updateStatus.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(id);
    },
  });

  const deleteMutation = trpc.package.delete.useMutation({
    onSuccess: () => {
      window.location.href = '/packages';
    },
  });

  const deleteItineraryMutation = trpc.package.deleteItineraryDay.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(id);
    },
  });

  const deleteMediaMutation = trpc.package.deleteMedia.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(id);
    },
  });

  const setMediaPrimaryMutation = trpc.package.setMediaPrimary.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(id);
    },
  });

  const deleteInclusionMutation = trpc.package.deleteInclusion.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(id);
    },
  });

  const deleteExclusionMutation = trpc.package.deleteExclusion.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(id);
    },
  });

  const deleteDepartureMutation = trpc.package.deleteDeparture.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(id);
    },
  });

  const handlePublish = () => {
    if (id) updateStatusMutation.mutate({ id, status: 'PUBLISHED' });
  };

  const handleArchive = () => {
    if (id) updateStatusMutation.mutate({ id, status: 'ARCHIVED' });
  };

  const handleDelete = () => {
    if (id && confirm(t('confirmDeletePackage'))) {
      deleteMutation.mutate(id);
    }
  };

  const handleAddItinerary = () => {
    setEditingItinerary(undefined);
    setIsItineraryModalOpen(true);
  };

  const handleEditItinerary = (day: ItineraryDay) => {
    setEditingItinerary(day);
    setIsItineraryModalOpen(true);
  };

  const handleDeleteItinerary = (dayId: string) => {
    if (confirm(t('confirmDeleteDay'))) {
      deleteItineraryMutation.mutate(dayId);
    }
  };

  const handleAddMedia = () => {
    setEditingMedia(undefined);
    setIsMediaModalOpen(true);
  };

  const handleEditMedia = (mediaItem: MediaItem) => {
    setEditingMedia(mediaItem);
    setIsMediaModalOpen(true);
  };

  const handleDeleteMedia = (mediaId: string) => {
    if (confirm(t('confirmDeleteImage'))) {
      deleteMediaMutation.mutate(mediaId);
    }
  };

  const handleSetMediaPrimary = (mediaId: string) => {
    setMediaPrimaryMutation.mutate(mediaId);
  };

  const handleAddInclusion = () => {
    setIsInclusionModalOpen(true);
  };

  const handleDeleteInclusion = (inclusionId: string) => {
    if (confirm(t('confirmDeleteInclusion'))) {
      deleteInclusionMutation.mutate(inclusionId);
    }
  };

  const handleAddExclusion = () => {
    setIsExclusionModalOpen(true);
  };

  const handleDeleteExclusion = (exclusionId: string) => {
    if (confirm(t('confirmDeleteExclusion'))) {
      deleteExclusionMutation.mutate(exclusionId);
    }
  };

  const handleAddDeparture = () => {
    setEditingDeparture(undefined);
    setIsDepartureModalOpen(true);
  };

  const handleEditDeparture = (departure: DepartureItem) => {
    setEditingDeparture(departure);
    setIsDepartureModalOpen(true);
  };

  const handleDeleteDeparture = (departureId: string) => {
    if (confirm(t('confirmDeleteDeparture'))) {
      deleteDepartureMutation.mutate(departureId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{t('loadingPackage')}</span>
      </div>
    );
  }

  if (error || !pkg) {
    return (
      <div className="space-y-4">
        <Link
          to="/travel/packages"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('backToPackages')}
        </Link>
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('packageNotFound')}</p>
          <p className="text-sm">{error?.message || t('packageNotExist')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Link */}
      <Link
        to="/travel/packages"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Packages
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{pkg.code}</span>
            <span className={`px-2 py-0.5 text-xs font-medium ${statusColors[pkg.status]}`}>
              {t(statusLabelKeys[pkg.status] || pkg.status)}
            </span>
            <span className={`px-2 py-0.5 text-xs font-medium ${difficultyColors[pkg.difficulty]}`}>
              {pkg.difficulty}
            </span>
            {pkg.isFeatured && (
              <span className="flex items-center gap-1 bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
                <Star className="h-3 w-3" />
                {t('featured')}
              </span>
            )}
          </div>
          <h1 className="text-2xl font-bold">{pkg.name}</h1>
          {pkg.shortDescription && (
            <p className="text-muted-foreground max-w-2xl">{pkg.shortDescription}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setIsEditModalOpen(true)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {pkg.status !== 'PUBLISHED' && (
                <DropdownMenuItem onClick={handlePublish}>
                  <Globe className="mr-2 h-4 w-4" />
                  {t('publish')}
                </DropdownMenuItem>
              )}
              {pkg.status !== 'ARCHIVED' && (
                <DropdownMenuItem onClick={handleArchive}>
                  <Archive className="mr-2 h-4 w-4" />
                  {t('archive')}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600" onClick={handleDelete}>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Modals */}
      {isEditModalOpen && (
        <PackageModal
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          package={{
            id: pkg.id,
            code: pkg.code,
            name: pkg.name,
            slug: pkg.slug,
            shortDescription: pkg.shortDescription,
            description: pkg.description,
            duration: pkg.duration,
            destinations: pkg.destinations,
            categoryId: pkg.categoryId,
            tags: pkg.tags,
            difficulty: pkg.difficulty as PackageDifficulty,
            status: pkg.status as PackageStatus,
            isFeatured: pkg.isFeatured,
            cancellationPolicy: pkg.cancellationPolicy,
          }}
          onSuccess={() => utils.package.byId.invalidate(id)}
        />
      )}

      {isPricingModalOpen && (
        <PricingModal
          open={isPricingModalOpen}
          onOpenChange={setIsPricingModalOpen}
          packageId={pkg.id}
          onSuccess={() => utils.package.byId.invalidate(id)}
        />
      )}

      {isItineraryModalOpen && (
        <ItineraryModal
          open={isItineraryModalOpen}
          onOpenChange={setIsItineraryModalOpen}
          packageId={pkg.id}
          itinerary={editingItinerary}
          onSuccess={() => utils.package.byId.invalidate(id)}
        />
      )}

      {isMediaModalOpen && (
        <MediaModal
          open={isMediaModalOpen}
          onOpenChange={setIsMediaModalOpen}
          packageId={pkg.id}
          media={editingMedia}
          onSuccess={() => utils.package.byId.invalidate(id)}
        />
      )}

      {isInclusionModalOpen && (
        <InclusionExclusionModal
          open={isInclusionModalOpen}
          onOpenChange={setIsInclusionModalOpen}
          packageId={pkg.id}
          type="inclusion"
          onSuccess={() => utils.package.byId.invalidate(id)}
        />
      )}

      {isExclusionModalOpen && (
        <InclusionExclusionModal
          open={isExclusionModalOpen}
          onOpenChange={setIsExclusionModalOpen}
          packageId={pkg.id}
          type="exclusion"
          onSuccess={() => utils.package.byId.invalidate(id)}
        />
      )}

      {isDepartureModalOpen && (
        <DepartureModal
          open={isDepartureModalOpen}
          onOpenChange={setIsDepartureModalOpen}
          packageId={pkg.id}
          packageDuration={pkg.duration}
          departure={editingDeparture}
          onSuccess={() => utils.package.byId.invalidate(id)}
        />
      )}

      {/* Tab Navigation */}
      <div className="border-b">
        <nav className="-mb-px flex gap-6">
          {tabDefs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:border-muted-foreground/30 hover:text-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
                {t(tab.labelKey)}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[500px]">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Package Info Card */}
              <div className="border border-border bg-card p-6">
                <h2 className="mb-4 font-semibold">{t('detail.packageDetails')}</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">{t('detail.duration')}</p>
                      <p className="font-medium">{pkg.duration} {t('days')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">{t('detail.destinations')}</p>
                      <p className="font-medium">{pkg.destinations.join(', ')}</p>
                    </div>
                  </div>
                  {pkg.category && (
                    <div className="flex items-center gap-3">
                      <Tag className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">{t('detail.category')}</p>
                        <p className="font-medium">{pkg.category.name}</p>
                      </div>
                    </div>
                  )}
                  {pkg.tags.length > 0 && (
                    <div className="flex items-center gap-3">
                      <Tag className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">{t('detail.tags')}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {pkg.tags.map((tag) => (
                            <span key={tag} className="bg-muted px-2 py-0.5 text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {pkg.description && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground mb-2">{t('detail.fullDescription')}</p>
                    <p className="text-sm whitespace-pre-wrap">{pkg.description}</p>
                  </div>
                )}
              </div>

              {/* Itinerary */}
              <div className="border border-border bg-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold flex items-center gap-2">
                    <List className="h-5 w-5" />
                    {t('itinerary')}
                  </h2>
                  <Button variant="outline" size="sm" onClick={handleAddItinerary}>
                    <Plus className="mr-2 h-4 w-4" />
                    {t('addDay')}
                  </Button>
                </div>
                {pkg.itinerary && pkg.itinerary.length > 0 ? (
                  <div className="space-y-4">
                    {pkg.itinerary
                      .sort((a, b) => a.dayNumber - b.dayNumber)
                      .map((day) => (
                        <div key={day.id} className="border border-border p-4 group">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <span className="flex h-6 w-6 items-center justify-center bg-primary text-xs text-primary-foreground flex-shrink-0 mt-0.5">
                                {day.dayNumber}
                              </span>
                              <div>
                                <h3 className="font-medium">{day.title}</h3>
                                {day.description && (
                                  <p className="text-sm text-muted-foreground mt-1">{day.description}</p>
                                )}
                                {day.activities && Array.isArray(day.activities) && day.activities.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {(day.activities as string[]).map((activity: string, idx: number) => (
                                      <span key={idx} className="bg-muted px-2 py-0.5 text-xs">
                                        {activity}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {day.meals && day.meals.length > 0 && (
                                  <p className="text-xs text-muted-foreground mt-2">
                                    {t('meals')}{day.meals.map(m => m.toLowerCase()).join(', ')}
                                  </p>
                                )}
                                {day.accommodation && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {t('stay')}{day.accommodation}
                                  </p>
                                )}
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditItinerary({
                                  ...day,
                                  activities: Array.isArray(day.activities) ? day.activities as string[] : [],
                                })}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => handleDeleteItinerary(day.id)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">{t('noItinerary')}</p>
                )}
              </div>

              {/* Inclusions & Exclusions */}
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="border border-border bg-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      {t('inclusions')}
                    </h2>
                    <Button variant="outline" size="sm" onClick={handleAddInclusion}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add
                    </Button>
                  </div>
                  {pkg.inclusions && pkg.inclusions.length > 0 ? (
                    <ul className="space-y-2">
                      {pkg.inclusions.map((item) => (
                        <li key={item.id} className="flex items-start justify-between gap-2 text-sm group">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                            {item.item}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleDeleteInclusion(item.id)}
                          >
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted-foreground text-sm">{t('noInclusions')}</p>
                  )}
                </div>
                <div className="border border-border bg-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold flex items-center gap-2">
                      <XCircle className="h-5 w-5 text-red-600" />
                      {t('exclusions')}
                    </h2>
                    <Button variant="outline" size="sm" onClick={handleAddExclusion}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add
                    </Button>
                  </div>
                  {pkg.exclusions && pkg.exclusions.length > 0 ? (
                    <ul className="space-y-2">
                      {pkg.exclusions.map((item) => (
                        <li key={item.id} className="flex items-start justify-between gap-2 text-sm group">
                          <div className="flex items-start gap-2">
                            <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                            {item.item}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleDeleteExclusion(item.id)}
                          >
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted-foreground text-sm">{t('noExclusions')}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar - Quick Stats */}
            <div className="space-y-6">
              <AllocationConfigSection
                packageId={pkg.id}
                onRefresh={() => utils.package.byId.invalidate(id)}
              />
            </div>
          </div>
        )}

        {/* Options & Variants Tab */}
        {activeTab === 'options' && (
          <div className="grid gap-6 lg:grid-cols-2">
            <OptionGroupsSection
              packageId={pkg.id}
              onRefresh={() => utils.package.byId.invalidate(id)}
            />
            <VariantSection
              packageId={pkg.id}
              onRefresh={() => utils.package.byId.invalidate(id)}
            />
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <div className="max-w-4xl">
            <div className="border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-semibold flex items-center gap-2 text-lg">
                    <DollarSign className="h-5 w-5" />
                    {t('pricingTiers')}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('pricingSubtitle')}
                  </p>
                </div>
                <Button onClick={() => setIsPricingModalOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('addPricing')}
                </Button>
              </div>
              {pkg.pricing && pkg.pricing.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {pkg.pricing.map((price) => (
                    <div key={price.id} className="border border-border p-4 hover:border-primary/50 transition-colors">
                      <div className="flex justify-between items-center mb-3">
                        <span className="font-medium text-lg">{price.seasonName || t('standardSeason')}</span>
                        {price.isActive && (
                          <span className="bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                            {t('active')}
                          </span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t('paxCount', { count: 2 })}</span>
                          <span className="font-medium">{formatCurrency(Number(price.price2Pax))}</span>
                        </div>
                        {price.price4Pax && (
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{t('paxCount', { count: 4 })}</span>
                            <span className="font-medium">{formatCurrency(Number(price.price4Pax))}</span>
                          </div>
                        )}
                        {price.price6Pax && (
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{t('paxCount', { count: 6 })}</span>
                            <span className="font-medium">{formatCurrency(Number(price.price6Pax))}</span>
                          </div>
                        )}
                        {price.singleSupplement && (
                          <div className="flex justify-between text-sm pt-2 border-t">
                            <span className="text-muted-foreground">{t('singleSupplement')}</span>
                            <span className="font-medium">{formatCurrency(Number(price.singleSupplement))}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <DollarSign className="h-12 w-12 text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">{t('noPricing')}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('addPricingToEnable')}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Media Tab */}
        {activeTab === 'media' && (
          <div className="max-w-5xl">
            <div className="border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-semibold flex items-center gap-2 text-lg">
                    <Image className="h-5 w-5" />
                    {t('mediaGallery')}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('mediaSubtitle')}
                  </p>
                </div>
                <Button onClick={handleAddMedia}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('addMedia')}
                </Button>
              </div>
              {pkg.media && pkg.media.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {pkg.media.map((item) => (
                    <div
                      key={item.id}
                      className="aspect-square bg-muted overflow-hidden relative group"
                    >
                      <img
                        src={item.url}
                        alt={item.alt || pkg.name}
                        className="h-full w-full object-cover"
                      />
                      {item.isPrimary && (
                        <span className="absolute top-2 left-2 bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
                          {t('primary')}
                        </span>
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/20">
                              <MoreHorizontal className="h-5 w-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditMedia(item)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            {!item.isPrimary && (
                              <DropdownMenuItem onClick={() => handleSetMediaPrimary(item.id)}>
                                <Star className="mr-2 h-4 w-4" />
                                {t('setAsPrimary')}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => handleDeleteMedia(item.id)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Image className="h-12 w-12 text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">{t('noMedia')}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('uploadImages')}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Departures Tab */}
        {activeTab === 'departures' && (
          <div className="max-w-4xl">
            <div className="border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-semibold flex items-center gap-2 text-lg">
                    <Calendar className="h-5 w-5" />
                    {t('scheduledDepartures')}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('departuresSubtitle')}
                  </p>
                </div>
                <Button onClick={handleAddDeparture}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('addDeparture')}
                </Button>
              </div>
              {pkg.departures && pkg.departures.length > 0 ? (
                <div className="space-y-3">
                  {pkg.departures.map((departure) => (
                    <div key={departure.id} className="border border-border p-4 group hover:border-primary/50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <div className="flex items-center gap-3">
                            <p className="font-medium text-lg">
                              {new Date(departure.departureDate).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </p>
                            {departure.isGuaranteed && (
                              <span className="bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                                {t('guaranteed')}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {t('returnDate')}{new Date(departure.returnDate).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                          <div className="flex items-center gap-4 mt-2">
                            <p className="text-sm flex items-center gap-1.5">
                              <Users className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{departure.bookedCount}</span>
                              <span className="text-muted-foreground">{t('capacityBooked', { capacity: departure.capacity })}</span>
                            </p>
                            {departure.notes && (
                              <p className="text-sm text-muted-foreground">
                                {t('note')}{departure.notes}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span
                            className={`px-2 py-0.5 text-xs font-medium ${
                              departure.status === 'OPEN'
                                ? 'bg-green-100 text-green-700'
                                : departure.status === 'LIMITED'
                                ? 'bg-yellow-100 text-yellow-700'
                                : departure.status === 'SOLD_OUT'
                                ? 'bg-gray-100 text-gray-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {departure.status.replace('_', ' ')}
                          </span>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditDeparture({
                                id: departure.id,
                                departureDate: new Date(departure.departureDate),
                                returnDate: new Date(departure.returnDate),
                                capacity: departure.capacity,
                                bookedCount: departure.bookedCount,
                                status: departure.status as DepartureStatus,
                                isGuaranteed: departure.isGuaranteed,
                                notes: departure.notes,
                              })}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => handleDeleteDeparture(departure.id)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Calendar className="h-12 w-12 text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">{t('noDepartures')}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('addDepartureDates')}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
